import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthService } from "../services/authService";
import { getThemeAssets } from "../utils/theme";

const Login: React.FC = () => {
  const [phone, setPhone] = useState("");
  const [otp, setOTP] = useState(["", "", "", "", "", ""]);
  const [isOTPSent, setIsOTPSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [otpCountdown, setOtpCountdown] = useState(0);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const otpInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const themeAssets = getThemeAssets();

  const bannerData = [
    {
      image: "/assets/banners/1.jpg",
      title: "Never Miss a Lead Again",
      subtitle: "Lead Management",
      description:
        "Track and nurture your leads from one place. Oversee and handle your entire lead pipeline. Maximize your lead conversion by transforming prospects into loyal customers with intelligent tracking and automated workflows.",
    },
    {
      image: "/assets/banners/2.jpg",
      title: "Visual Pipeline Management",
      subtitle: "Sales Pipeline",
      description:
        "Manage your sales pipeline with intuitive drag-and-drop boards. Track deals through every stage, identify bottlenecks, and accelerate your sales cycle with real-time pipeline visibility and insights.",
    },
    {
      image: "/assets/banners/3.jpg",
      title: "Streamline Your Sales Process",
      subtitle: "Pipeline Management",
      description:
        "Visualize and optimize your entire sales pipeline from first contact to closed deal. Move leads seamlessly through stages, track progress in real-time, and ensure no opportunity falls through the cracks.",
    },
    {
      image: "/assets/banners/4.jpg",
      title: "Enterprise-Grade Security",
      subtitle: "Secure & Reliable",
      description:
        "Your data is protected with industry-leading security measures. Enjoy peace of mind with encrypted connections, role-based access controls, and regular backups.",
    },
  ];

  // Auto-rotate carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % bannerData.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [bannerData.length]);

  // Pre-fill phone from URL param (e.g. ?phone=9876543210 passed from website after claim/add school)
  useEffect(() => {
    const phoneParam = searchParams.get("phone");
    if (phoneParam && /^\d{10}$/.test(phoneParam)) {
      setPhone(phoneParam);
    }
  }, [searchParams]);

  // Clear session on login page load
  useEffect(() => {
    const cookies = document.cookie.split(";");
    for (let cookie of cookies) {
      const cookieName = cookie.split("=")[0].trim();
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.yellowslate.com`;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=yellowslate.com`;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.crm.yellowslate.com`;
    }
    ["crm_user", "crm_user_column_preferences", "crm_user_data"].forEach((item) =>
      localStorage.removeItem(item)
    );
  }, []);

  // Focus first OTP input when OTP is sent
  useEffect(() => {
    if (isOTPSent && otpInputRefs.current[0]) {
      otpInputRefs.current[0].focus();
    }
  }, [isOTPSent]);

  const startCountdown = (seconds = 120) => {
    if (countdownRef.current) clearInterval(countdownRef.current);
    setOtpCountdown(seconds);
    countdownRef.current = setInterval(() => {
      setOtpCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current!);
          countdownRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Cleanup countdown on unmount
  useEffect(() => {
    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, []);

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^\d{10}$/.test(phone)) {
      setError("Please enter a valid 10-digit mobile number");
      return;
    }
    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:8000/api";
      const response = await fetch(`${apiUrl}/auth/send-whatsapp-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Failed to send OTP");
      setIsOTPSent(true);
      setSuccessMessage("OTP sent! Please check your WhatsApp.");
      startCountdown();
    } catch (err: any) {
      setError(err.message || "Failed to send OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOTPChange = (index: number, value: string) => {
    if (value && !/^\d+$/.test(value)) return;
    const newOTP = [...otp];
    newOTP[index] = value;
    setOTP(newOTP);
    if (value && index < 5) otpInputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    const newOTP = [...otp];
    for (let i = 0; i < pastedData.length; i++) newOTP[i] = pastedData[i];
    setOTP(newOTP);
    otpInputRefs.current[Math.min(pastedData.length, 5)]?.focus();
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    const otpCode = otp.join("");
    if (otpCode.length !== 6) {
      setError("Please enter the complete 6-digit OTP");
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:8000/api";
      const response = await fetch(`${apiUrl}/auth/verify-whatsapp-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone, otp: otpCode }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Invalid OTP");
      if (data.status === "success" && data.data?.token) {
        AuthService.setToken(data.data.token, { expires: 7 });
        AuthService.setUserData(data.data.user);
        localStorage.setItem("crm_user", JSON.stringify(data.data.user));
        const clientId = data.data.user?.client_id_fk;
        navigate(clientId === 9 || clientId === "9" ? "/leads_admin" : "/leads");
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (err: any) {
      setError(err.message || "OTP verification failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setOTP(["", "", "", "", "", ""]);
    setError(null);
    setSuccessMessage(null);
    setIsLoading(true);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:8000/api";
      const response = await fetch(`${apiUrl}/auth/send-whatsapp-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Failed to resend OTP");
      setSuccessMessage("OTP resent! Please check your WhatsApp.");
      startCountdown();
    } catch (err: any) {
      setError(err.message || "Failed to resend OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const clientId =
      import.meta.env.VITE_GOOGLE_CLIENT_ID ||
      "648675563452-ffo4t8hkfovc1kq0qfmls26hvcn76u5o.apps.googleusercontent.com";
    const redirectUri = encodeURIComponent(
      import.meta.env.VITE_GOOGLE_CALLBACK_URL || "http://localhost:8000/api/auth/google/callback"
    );
    const state = Math.random().toString(36).substring(7);
    sessionStorage.setItem("google_oauth_state", state);
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&scope=email%20profile&state=${state}`;
  };

  return (
    <div className="min-h-screen lg:h-screen w-screen flex flex-col-reverse lg:flex-row m-0 p-0">
      {/* Left Side - Image Carousel */}
      <div className="w-full h-[100vh] lg:w-1/2 lg:h-screen relative overflow-hidden bg-gray-900">
        {bannerData.map((banner, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
              index === currentSlide ? "opacity-100" : "opacity-0"
            }`}
          >
            <img src={banner.image} alt={banner.title} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
            <div className="absolute inset-0 flex flex-col justify-end p-12 text-white">
              <div className="max-w-2xl">
                <p className="text-sm font-medium text-yellow-400 mb-2 uppercase tracking-wider">
                  {banner.subtitle}
                </p>
                <h2 className="text-4xl font-bold mb-4 leading-tight">{banner.title}</h2>
                <p className="text-base text-gray-200 leading-relaxed mb-6">{banner.description}</p>
              </div>
            </div>
          </div>
        ))}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-2 z-20">
          {bannerData.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentSlide ? "bg-white w-8" : "bg-white/50 hover:bg-white/75"
              }`}
            />
          ))}
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 min-h-screen lg:h-screen bg-white flex items-center justify-center px-4 py-8 sm:px-6 lg:px-0 overflow-y-auto">
        <div className="w-full max-w-md mx-auto lg:mx-8">
          {/* Logo + Heading */}
          <div className="text-center mb-5">
            <img src={themeAssets.loginLogo} alt={themeAssets.brandName} className="h-10 sm:h-12 mx-auto mb-4" />
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">
              Welcome to {themeAssets.brandName}
            </h1>
            <p className="text-sm text-gray-500">
              {!isOTPSent ? "Sign in with your WhatsApp number" : `OTP sent to +91 ${phone}`}
            </p>
          </div>

          {/* Tip for school owners */}
          <div className="mb-5 p-3 rounded-lg border border-green-200 bg-green-50 flex items-start gap-3">
            <span className="text-green-600 text-xl mt-0.5">📱</span>
            <div>
              <p className="text-sm font-semibold text-green-800 mb-0.5">
                Registered your school on YellowSlate?
              </p>
              <p className="text-xs text-green-700 mb-0">
                Enter your registered phone number below.
              </p>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* Success */}
          {successMessage && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-700 text-sm">{successMessage}</p>
            </div>
          )}

          {/* Phase 1: Phone input */}
          {!isOTPSent && (
            <form onSubmit={handleSendOTP} className="space-y-4">
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Mobile Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                  placeholder="Enter your mobile number"
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                  disabled={isLoading}
                  maxLength={10}
                  autoFocus
                />
              </div>

              <button
                type="submit"
                disabled={isLoading || phone.length !== 10}
                className="w-full bg-[#25D366] hover:bg-[#1da851] text-white py-3 px-4 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#25D366] disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 transition-all duration-200 shadow-lg flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending OTP...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                    </svg>
                    Send OTP via WhatsApp
                  </>
                )}
              </button>
            </form>
          )}

          {/* Phase 2: OTP input */}
          {isOTPSent && (
            <form onSubmit={handleVerifyOTP} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-4 text-center">
                  Enter 6-Digit OTP
                </label>
                <div className="flex justify-center gap-2" onPaste={handlePaste}>
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => { otpInputRefs.current[index] = el; }}
                      type="text"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOTPChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      className="w-11 h-11 sm:w-12 sm:h-12 text-center text-xl font-bold border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                      disabled={isLoading}
                    />
                  ))}
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading || otp.join("").length !== 6}
                className="w-full bg-[#25D366] hover:bg-[#1da851] text-white py-3 px-4 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 transition-all duration-200 shadow-lg"
              >
                {isLoading ? "Verifying..." : "Verify & Sign In"}
              </button>

              <div className="flex justify-between text-sm">
                <button
                  type="button"
                  onClick={() => { setIsOTPSent(false); setOTP(["", "", "", "", "", ""]); setError(null); setSuccessMessage(null); }}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  ← Change number
                </button>
                <button
                  type="button"
                  onClick={handleResendOTP}
                  disabled={isLoading || otpCountdown > 0}
                  className="text-[#25D366] hover:text-[#1da851] font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {otpCountdown > 0
                    ? `Resend in ${Math.floor(otpCountdown / 60)}:${String(otpCountdown % 60).padStart(2, "0")}`
                    : "Resend OTP"}
                </button>
              </div>
            </form>
          )}

          {/* Divider */}
          <div className="my-5 flex items-center">
            <div className="flex-1 border-t border-gray-200"></div>
            <span className="px-4 text-xs text-gray-400">or continue with</span>
            <div className="flex-1 border-t border-gray-200"></div>
          </div>

          {/* Secondary options: Google + Sign in with Email side by side */}
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={handleGoogleLogin}
              disabled={isLoading}
              className="bg-white hover:bg-gray-50 text-gray-700 py-2.5 px-3 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2 border border-gray-300 text-sm"
              title="Sign in with Google"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Google
            </button>

            <button
              onClick={() => navigate("/login-email")}
              disabled={isLoading}
              className="bg-white hover:bg-gray-50 text-gray-700 py-2.5 px-3 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2 border border-gray-300 text-sm"
              title="Sign in with Email"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Sign in with Email
            </button>
          </div>

          <p className="mt-5 text-center text-xs text-gray-400">
            By continuing, you agree to our{" "}
            <a href="#" className="text-blue-500 hover:text-blue-600">Terms of Service</a>{" "}
            and{" "}
            <a href="#" className="text-blue-500 hover:text-blue-600">Privacy Policy</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
